
import { Favorite, FavoriteBorder } from "@mui/icons-material";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthModal from "../../Auth/AuthModal";
import { useDispatch, useSelector } from "react-redux";
import {
  addItemToWishlist,
  isWishlistItemExist,
  removeWishlistItem
} from "../../State/Wishlist/Action";
import { getUser } from "../../State/Auth/Action";
import { useConvertPrice } from "../../../Hooks/useConvertPrice";
import defaultImage from "../../../Common/productDefaultImage.png"

const HomeSectionCard = ({ product }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const jwt = localStorage.getItem("jwt");
  const role = localStorage.getItem("role");
  const [openAuthModal, setOpenAuthModal] = useState(false);
  const [isInWishlist, setIsInWishlist] = useState(false);
  const wishlistLoading = useSelector((state) => state.wishlist.loading);
  const { convert, customerPriceWithCharge, payableAmount, selectedCurrency } = useConvertPrice();
  const [loadingProductId, setLoadingProductId] = useState(null);
  const { wishlist } = useSelector((store) => store);

  const [slideIndex, setSlideIndex] = useState(0);

  const handleOpenAuthModal = () => setOpenAuthModal(true);
  const handleCloseAuthModal = () => setOpenAuthModal(false);

  useEffect(() => {
    if (jwt && role === "CUSTOMER") {
      dispatch(getUser(jwt));
    }
  }, [jwt, dispatch, role]);

  useEffect(() => {
    if (jwt && role === "CUSTOMER" && product.id) {
      dispatch(isWishlistItemExist(product.id)).then((exists) => {
        if (typeof exists === "boolean") {
          setIsInWishlist(exists);
        }
      });
    }
  }, [dispatch, product.id, jwt, role, wishlist?.deletedWishlistItem, wishlist?.addWislistItem]);

  const handleFavorite = async (e) => {
    e.stopPropagation();
    e.preventDefault();

    const productId = product.id;
    setLoadingProductId(productId); // ✅ सिर्फ इसी card को loading बनाओ

    try {
      if (!jwt || role !== "CUSTOMER") {
        sessionStorage.setItem("pendingWishlistProduct", productId);
        handleOpenAuthModal();
        return;
      }

      if (isInWishlist) {
        await dispatch(removeWishlistItem(productId));
        setIsInWishlist(false);
        setLoadingProductId(null); // ✅ काम पूरा होने पर reset
      } else {
        await dispatch(addItemToWishlist(productId));
        setIsInWishlist(true);
        setLoadingProductId(null); // ✅ काम पूरा होने पर reset
      }
    } finally {
      setLoadingProductId(null); // ✅ काम पूरा होने पर reset
    }
  };


  useEffect(() => {
    const pendingProductId = sessionStorage.getItem("pendingWishlistProduct");
    if (pendingProductId && jwt && role === "CUSTOMER") {
      dispatch(addItemToWishlist(pendingProductId));
      setIsInWishlist(true);
      sessionStorage.removeItem("pendingWishlistProduct");
    }
  }, [jwt, role]);

  const primaryImages = useMemo(() => {
    return product.images?.filter((img) => img.isPrimary) || [];
  }, [product.images]);

  useEffect(() => {
    if (primaryImages.length > 1) {
      const interval = setInterval(() => {
        setSlideIndex((prev) => (prev + 1) % primaryImages.length);
      }, 4000); // change every 2 seconds

      return () => clearInterval(interval);
    }
  }, [primaryImages]);

  const currentImage = primaryImages.length > 0
    ? primaryImages[slideIndex]?.fullUrl
    : defaultImage;

  return (
    product.id && (
      <>
        <div
          key={`product-${product.id}`}
          className="w-full"
          onClick={() => navigate(`/product/${product.id}`)}
        >
          <div className="group cursor-pointer overflow-hidden flex flex-col bg-transparent">
            <div className="relative aspect-[3/4] bg-neutral-100 overflow-hidden rounded-2xl">
              <img
                src={currentImage}
                alt={product.title}
                className="object-cover w-full h-full transition-transform duration-500 ease-out group-hover:scale-[1.03]"
                loading="lazy"
              />
              <button
                onClick={handleFavorite}
                className="group/heart absolute top-3 right-3 w-8 h-8 rounded-full bg-white/95 shadow-sm flex items-center justify-center border-0"
                disabled={loadingProductId === product.id || wishlistLoading}
              >
                {loadingProductId === product.id && wishlistLoading ? (
                  <span className="inline-block w-4 h-4 border-2 border-stone-400 border-t-transparent rounded-full animate-spin"></span>
                ) : isInWishlist ? (
                  <Favorite className="text-red-500" style={{ fontSize: 18 }} />
                ) : (
                  <FavoriteBorder className="text-stone-400 group-hover/heart:text-red-500" style={{ fontSize: 18 }} />
                )}
              </button>
            </div>

            <div className="flex flex-col flex-1 pt-3 px-0.5 text-left">
              <h3 className="font-sans text-[14px] font-semibold text-stone-800 leading-tight truncate">
                {product?.brand?.name || product?.title?.split(' ').slice(0, 2).join(' ') || "Brand"}
              </h3>
              <p className="mt-0.5 font-sans text-[13px] text-stone-500 leading-snug line-clamp-1">
                {product?.title || product?.description || ""}
              </p>

              {(() => {
                const cheapestVariant = product?.variants?.reduce(
                  (min, curr) =>
                    curr?.adminPrice < min?.adminPrice ? curr : min,
                  product?.variants?.[0]
                );

                if (!cheapestVariant) return null;

                const hasDiscount = cheapestVariant.viewTotalDiscount > 0;
                const originalPrice = convert(
                  customerPriceWithCharge(selectedCurrency, cheapestVariant.viewCustomerAmount)
                );
                const discountedPrice = payableAmount(
                  customerPriceWithCharge(selectedCurrency, cheapestVariant.viewCustomerAmount),
                  cheapestVariant.viewTotalDiscount
                );

                return (
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 mt-1.5 font-sans">
                    {hasDiscount ? (
                      <>
                        <span className="text-[14px] font-semibold text-stone-900">
                          {discountedPrice} {selectedCurrency}
                        </span>
                        <span className="text-[13px] text-stone-400 line-through">
                          {originalPrice} {selectedCurrency}
                        </span>
                        <span className="text-[13px] font-medium text-[#2f9e44]">
                          {cheapestVariant.viewTotalDiscount}% OFF
                        </span>
                      </>
                    ) : (
                      <span className="text-[14px] font-semibold text-stone-900">
                        {originalPrice} {selectedCurrency}
                      </span>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
        <AuthModal handleClose={handleCloseAuthModal} open={openAuthModal} />
      </>
    )
  );
};

export default HomeSectionCard;
